export const RSC_CACHE_STATUSES = [
  'HIT',
  'MISS',
  'BYPASS',
  'DYNAMIC',
  'EXPIRED',
  'REVALIDATED',
  'UPDATING',
  'STALE',
] as const
export type RscCacheStatus = (typeof RSC_CACHE_STATUSES)[number] | 'UNKNOWN' | 'MIXED'
export type RscDelivery = 'network' | 'browser_cache' | 'unknown'

type Observation = { url: string; startedAt: number; headersAt: number; status: RscCacheStatus }
const observations: Observation[] = []
const resourceTimings: PerformanceResourceTiming[] = []
const MAX_OBSERVATIONS = 100
let installed:
  { original: typeof fetch; wrapped: typeof fetch; users: number; stop: () => void; drain: () => void } | undefined

export function rscRouteKey(href: string): string {
  const url = new URL(href, window.location.href)
  url.searchParams.delete('_rsc')
  url.searchParams.sort()
  return `${url.pathname.replace(/\/+$/, '') || '/'}${url.search}`
}

function requestUrl(input: RequestInfo | URL, init?: RequestInit): URL | undefined {
  try {
    const request = typeof Request !== 'undefined' && input instanceof Request ? input : undefined
    if ((init?.method ?? request?.method ?? 'GET').toUpperCase() !== 'GET') return
    const url = new URL(request?.url ?? String(input), window.location.href)
    if (url.origin !== window.location.origin) return
    const headers = new Headers(init?.headers ?? request?.headers)
    if (url.searchParams.has('_rsc') || headers.get('RSC') === '1') return url
  } catch {
    // Telemetry must never prevent the original fetch from running.
  }
}

export function installRscCacheObserver(): () => void {
  if (installed) {
    installed.users++
  } else {
    const original = window.fetch
    let observing = true
    const wrapped: typeof fetch = function (this: unknown, input, init) {
      const url = observing ? requestUrl(input, init) : undefined
      if (!url) return original.call(this, input, init)
      const startedAt = performance.now()
      const request = original.call(this, input, init)
      return request.then((response) => {
        try {
          if (!observing) return response
          if (response.url && new URL(response.url).origin !== window.location.origin) return response
          const raw = response.headers.get('CF-Cache-Status')?.trim().toUpperCase()
          const status: RscCacheStatus = RSC_CACHE_STATUSES.find((known) => known === raw) ?? 'UNKNOWN'
          observations.push({
            url: rscRouteKey(response.url || url.href),
            startedAt,
            headersAt: performance.now(),
            status,
          })
          if (observations.length > MAX_OBSERVATIONS) observations.splice(0, observations.length - MAX_OBSERVATIONS)
        } catch {
          // Only safe response metadata is observed; the response is returned unchanged.
        }
        return response
      })
    }
    const recordResources = (entries: PerformanceEntry[]) => {
      if (!observing) return
      for (const entry of entries) {
        if (!entry.name.includes('_rsc')) continue
        try {
          const url = new URL(entry.name)
          if (url.origin === window.location.origin && url.searchParams.has('_rsc'))
            resourceTimings.push(entry as PerformanceResourceTiming)
        } catch {
          /* Ignore non-URL resource entries. */
        }
      }
      if (resourceTimings.length > MAX_OBSERVATIONS)
        resourceTimings.splice(0, resourceTimings.length - MAX_OBSERVATIONS)
    }
    let resourceObserver: PerformanceObserver | undefined
    try {
      if (typeof PerformanceObserver !== 'undefined') {
        resourceObserver = new PerformanceObserver((list) => recordResources(list.getEntries()))
        resourceObserver.observe({ type: 'resource', buffered: true })
      }
    } catch {
      resourceObserver?.disconnect()
      resourceObserver = undefined
    }
    installed = {
      original,
      wrapped,
      users: 1,
      drain: () => recordResources(resourceObserver?.takeRecords() ?? []),
      stop: () => {
        observing = false
        resourceObserver?.disconnect()
        resourceTimings.length = 0
      },
    }
    window.fetch = wrapped
  }
  const installation = installed
  let released = false
  return () => {
    if (released) return
    released = true
    if (--installation.users !== 0) return
    installation.stop()
    if (window.fetch === installation.wrapped) window.fetch = installation.original
    installed = undefined
    observations.length = 0
  }
}

export function getRscCacheDetails(destination: string, startedAt: number, endedAt: number) {
  const key = rscRouteKey(destination)
  installed?.drain()
  const resources = new Map(
    [...resourceTimings, ...performance.getEntriesByType('resource')].map((entry) => [
      `${entry.name}:${entry.startTime}`,
      entry as PerformanceResourceTiming,
    ])
  )
  const entries = [...resources.values()].filter((entry) => {
    try {
      const url = new URL(entry.name)
      return (
        url.origin === window.location.origin &&
        url.searchParams.has('_rsc') &&
        rscRouteKey(entry.name) === key &&
        entry.startTime >= startedAt &&
        entry.responseEnd > 0 &&
        entry.responseEnd <= endedAt
      )
    } catch {
      return false
    }
  })
  const matched = observations.filter(
    (observation) => observation.url === key && observation.startedAt >= startedAt && observation.headersAt <= endedAt
  )
  const statuses = new Set(matched.map((observation) => observation.status))
  const status: RscCacheStatus =
    statuses.size === 1 && matched.length >= entries.length
      ? matched[0].status
      : statuses.size > 1
        ? 'MIXED'
        : 'UNKNOWN'
  const browserEntries = entries.filter((entry) => entry.transferSize === 0 && entry.decodedBodySize > 0)
  const delivery: RscDelivery =
    entries.length && browserEntries.length === entries.length
      ? 'browser_cache'
      : entries.length &&
          browserEntries.length === 0 &&
          entries.every((entry) => entry.transferSize > 0 && !entry.workerStart)
        ? 'network'
        : 'unknown'
  return {
    rsc_cache_status: status,
    rsc_delivery: delivery,
    rsc_request_count: Math.max(matched.length, entries.length),
    ...(entries.length ? { rsc_ms: Math.round(Math.max(...entries.map((entry) => entry.duration))) } : {}),
  }
}
